import React, { memo, useEffect, useState, useMemo, FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce } from 'react-use'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getInputIcon,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { Box } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import useDebouncedCallback from 'use-debounce/lib/useDebouncedCallback'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import * as Types from 'apps/fleet-planner/types'
import { fetchEquipment } from 'apps/fleet-planner/utils/actions/equipment'
import { groupItemsByMotorCarrierId } from 'apps/fleet-planner/utils/motorCarrierHelpers'
import { selectEquipmentState } from 'apps/fleet-planner/utils/selectors/equipment'
import { getEntityById } from 'utils/getEntityById'

export interface ITruckAutoSuggestProps extends IAutosuggestWrapperProps<Types.ITruck> {
  selectedTrackingMethod: TRACKING_METHOD_TYPE
  /**
   * @default 10
   */
  limitOptions?: number
}

interface IDescriptionProps {
  isELDConnected: boolean
}

const Description: FC<IDescriptionProps> = ({ isELDConnected }) => (
  <>{isELDConnected && <Box color={brandColors.infoDark}>(ELD connected)</Box>}</>
)

export const TruckAutoSuggest = memo<ITruckAutoSuggestProps>(
  ({
    dataTest,
    disabled: disabledProp,
    error: errorProp,
    large: largeProp,
    limitOptions = 20,
    onBlur: onBlurProp,
    onCancel: onCancelProp,
    onChange: onChangeProp,
    onInputChange: onInputChangeProp,
    selectedMotorCarrierId,
    selectedTrackingMethod,
    value: valueProp,
  }) => {
    const { isMobile } = useBreakpointContext()
    const { trucks } = useSelector(selectEquipmentState)
    const dispatch = useDispatch()

    const [inputValue, setInputValue] = useState<string>('')

    // Fetch trucks on mount
    useEffectOnce(() => {
      if (!trucks?.length) dispatch(fetchEquipment())
    })

    const [debouncedFetchEquipment] = useDebouncedCallback(value => {
      dispatch(fetchEquipment(undefined, { truckNumber: value }))
    }, 500)

    // Fetch and filter by truck number on inputValue change
    useEffect(() => {
      if (inputValue && inputValue.length >= 3) {
        debouncedFetchEquipment(inputValue)
      }
    }, [debouncedFetchEquipment, dispatch, inputValue, trucks.length])

    const changeHandler = (option: IAutocompleteOption | undefined) => {
      const newTruck = getEntityById(trucks, option?.id)
      onChangeProp(newTruck)
    }

    const options: IAutocompleteOption[] = useMemo(() => {
      let currentTrucks

      if (!selectedMotorCarrierId) {
        currentTrucks = trucks
      } else {
        /**
         * Only trigger this method if `filterByCarrier` is true,
         * since it requires some mapping of potentially many entries
         */
        const { associated = [] } = groupItemsByMotorCarrierId(trucks, [selectedMotorCarrierId])
        currentTrucks = associated
      }

      currentTrucks = currentTrucks.slice(0, limitOptions)

      const isEldConnectedField: keyof Types.ITruck =
        selectedTrackingMethod === 'keep_truckin' ? 'keepTruckinId' : 'isP44Valid'

      return currentTrucks.map(({ id = '', truckNumber: text, ...truck }: Types.ITruck) => ({
        id,
        text,
        groupIndex: 1,
        iconName: 'TruckIcon',
        description:
          selectedTrackingMethod !== 'mobile' ? (
            <Description isELDConnected={Boolean(truck[isEldConnectedField])} />
          ) : null,
      }))
    }, [selectedMotorCarrierId, limitOptions, selectedTrackingMethod, trucks])

    const inputChangeHandler = (val: string = '') => {
      setInputValue(val)
      if (onInputChangeProp) onInputChangeProp(val)
    }

    const icon = getInputIcon('search', inputValue)

    return (
      <Autosuggest
        id='truck'
        large={largeProp}
        label='Truck'
        placeholder='Search by truck #'
        icon={icon}
        renderOptionType='withIconAndDescription'
        value={valueProp}
        options={options}
        onChange={changeHandler}
        onInputChange={inputChangeHandler}
        onCancel={onCancelProp}
        dataTest={dataTest}
        clearable
        onBlur={onBlurProp}
        error={errorProp}
        disabled={disabledProp}
        selectOnFocus={!isMobile}
      />
    )
  }
)
